// Shared logic for the two restaurant detail pages: given the full set of
// `user_picks` + `profiles` rows and a target `place_id`, build the
// restaurant's display detail plus the list of recommenders who picked it,
// each with their 1-based rank. Both /restaurant/:slug (name-resolved) and
// /place/:placeId (place_id-resolved) funnel through `buildRestaurantDetail`
// so the rank computation stays in one place.

import { mapsUrlForPick } from './recommenderShape';
import { isFullList } from './fullList';
import type { Recommender } from '../types';

export interface RestaurantDetail {
  name: string;
  cuisine: string;
  location: string;
  placeId: string;
  googleMapsUrl: string;
  recommendersWithRanks: Array<{
    recommender: Recommender;
    rank: number;
  }>;
}

export interface RawPickRow {
  user_id: string;
  name: string;
  place_id: string;
  cuisine: string | null;
  location: string | null;
  position: number;
}

export interface RawProfileRow {
  user_id: string;
  slug: string;
  display_name: string;
}

export function isPickRow(value: unknown): value is RawPickRow {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.user_id === 'string' &&
    typeof v.name === 'string' &&
    typeof v.place_id === 'string' &&
    typeof v.position === 'number'
  );
}

export function isProfileRow(value: unknown): value is RawProfileRow {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.user_id === 'string' &&
    typeof v.slug === 'string' &&
    typeof v.display_name === 'string'
  );
}

// Returns null when no one has picked the given place_id.
export function buildRestaurantDetail(
  allPicks: RawPickRow[],
  allProfiles: RawProfileRow[],
  placeId: string
): RestaurantDetail | null {
  // Pre-compute each user's sorted pick list so we can convert any pick's
  // raw `position` into a contiguous 1..5 rank — `position` can be gappy
  // after a remove + add cycle.
  const sortedPicksByUser = new Map<string, RawPickRow[]>();
  for (const p of allPicks) {
    const list = sortedPicksByUser.get(p.user_id) ?? [];
    list.push(p);
    sortedPicksByUser.set(p.user_id, list);
  }
  sortedPicksByUser.forEach((list) => list.sort((a, b) => a.position - b.position));

  // Only full top fives are public: picks from someone still building their
  // list don't count here, mirroring the gated aggregated_picks view so the
  // pick count and recommender list on this page can never disagree with
  // the homepage ranking.
  const matchingPicks = allPicks.filter(
    (p) =>
      p.place_id === placeId &&
      isFullList(sortedPicksByUser.get(p.user_id)?.length ?? 0)
  );
  if (matchingPicks.length === 0) return null;

  const profileById = new Map<string, RawProfileRow>();
  for (const p of allProfiles) profileById.set(p.user_id, p);

  const recommendersWithRanks = matchingPicks
    .map((pick) => {
      const profile = profileById.get(pick.user_id);
      if (!profile) return null;
      const userPicks = sortedPicksByUser.get(pick.user_id) ?? [];
      const indexInUserList = userPicks.findIndex((p) => p.place_id === placeId);
      if (indexInUserList === -1) return null;
      const recommender: Recommender = {
        id: profile.user_id,
        name: profile.display_name,
        slug: profile.slug,
        // The detail pages only render identity fields off the recommender;
        // we don't need to ship their full picks here.
        restaurants: [],
      };
      return { recommender, rank: indexInUserList + 1 };
    })
    .filter((entry): entry is { recommender: Recommender; rank: number } => entry !== null)
    .sort((a, b) => a.rank - b.rank);

  const sample = matchingPicks[0];
  const name = sample.name;
  const cuisine = sample.cuisine ?? 'Restaurant';
  const location = sample.location ?? '';

  return {
    name,
    cuisine,
    location,
    placeId,
    googleMapsUrl: mapsUrlForPick({ name, location, placeId }),
    recommendersWithRanks,
  };
}
